import React, { useEffect, useRef } from 'react';
import cx from 'classnames';
import Icon from '../../../Icon';
import commonStyles from '../../Select.module.scss';
import styles from './GraphicalSelect.module.scss';
import { GraphicalSelectProps } from './GraphicalSelect.types';
import dispatchDOMEvent from '../../../../utils/dispatchDomEvent';
import useWindowDimensions from '../../../../utils/useWindowDimensions';

const arrowIcon = {
  width: '14',
  height: '8',
  fill: '',
  icon: 'chevron',
  viewBox: '0 0 14 8',
  title: 'Arrow',
  className: '',
  styleProps: {},
  stroke: '',
  svg: [''],
};

const GraphicalSelect = ({
  dataPointer,
  onChange,
  selectbox,
  graphicalSelect,
  optionsContainer,
  closeOptions,
  openOptions,
  selectValue,
  selectText,
  renderOptionsList,
  handleKeyDown,
  handleFocus,
  handleSelect,
  optionsOpen,
  controlledText,
  isControlled,
  className,
  placeholder,
  dataCy = 'graphicalSelect',
  optionsLength,
  setOpenAbove,
  openAbove,
  color = 'primary',
  invalid,
  errorMsgHolderId,
  id,
  variant,
}: GraphicalSelectProps) => {
  const hiddenSelect = useRef(null);
  const { height } = useWindowDimensions();
  useEffect(() => {
    if (selectValue !== null) {
      /* Use a hidden select to to store the actual values
       */
      dispatchDOMEvent(hiddenSelect.current, 'change');
    }
  }, [selectValue]);

  const onBlur = ({ relatedTarget, currentTarget }) => {
    const node = relatedTarget && relatedTarget.parentNode;
    const nodeIsCurrentTarget = node === currentTarget;
    const focusedOnSelectBox = relatedTarget === selectbox.current;
    if (nodeIsCurrentTarget) return;
    if (focusedOnSelectBox) return;
    closeOptions();
  };

  const handleListboxClick = () => (optionsOpen ? closeOptions() : openOptions());

  const scrollHandler = () => {
    if (!graphicalSelect.current) {
      return;
    }

    const { y, height: selectHeight } = graphicalSelect.current.getBoundingClientRect();
    const spaceLeft = height - y;
    const maxHeight = selectHeight * 5;
    const optionsTotalHeight = Math.min(selectHeight * optionsLength + selectHeight, maxHeight);
    setOpenAbove(!(optionsTotalHeight < spaceLeft));
  };

  useEffect(() => {
    window.addEventListener('scroll', scrollHandler, true);
    return () => {
      window.removeEventListener('scroll', scrollHandler, true);
    };
  }, []);

  useEffect(() => {
    scrollHandler();
  }, []);

  const returnOptionsStyle = (): string => {
    if (openAbove && optionsLength > 3) return styles.has3orMoreOptions;
    if (openAbove && optionsLength > 2) return styles.has2options;
    if (openAbove && optionsLength > 1) return styles.has1option;
    return '';
  };

  return (
    <div
      className={cx(
        styles.graphicalSelectWrapper,
        commonStyles[`graphicalSelectWrapper--${variant}`],
        className,
        {
          [commonStyles.open]: optionsOpen,
          [commonStyles.openAbove]: openAbove,
        },
        returnOptionsStyle()
      )}
      ref={graphicalSelect}>
      <div className={commonStyles[color]}>
        <div
          className={cx(commonStyles.graphicalSelect, {
            [styles.invalid]: invalid,
          })}
          onClick={handleListboxClick}
          role='combobox'
          aria-expanded={optionsOpen}
          aria-controls={id}
          aria-label='list'
          tabIndex={0}
          ref={selectbox}
          onKeyDown={(event) => handleKeyDown(event, null)}
          data-testid='graphicalSelect'
          data-cy={dataCy}
          aria-errormessage={errorMsgHolderId}>
          {isControlled ? controlledText || placeholder : selectText || selectValue || placeholder}
          <div className={cx(styles.iconContainer, commonStyles.iconContainer)} aria-hidden='true'>
            <Icon {...arrowIcon} />
          </div>
          {renderOptionsList({
            selectValue,
            optionsOpen,
            closeOptions,
            handleSelect,
            selectbox,
            graphicalSelect,
            optionsContainer,
            handleKeyDown,
            handleFocus,
            onBlur,
          })}
        </div>
        <select
          aria-hidden
          className={styles.hiddenSelect}
          onChange={onChange}
          ref={hiddenSelect}
          data-pointer={dataPointer}
          aria-invalid={invalid}>
          <option value={selectValue || ''}>{selectText}</option>
        </select>
      </div>
    </div>
  );
};

export default GraphicalSelect;
